import { Dispatch, SetStateAction } from "react";
import { Button, FoodsDeleteButton, FoodsIcon, FoodsTags, Typography } from "@foodpilot/foods";
import { Box, Stack, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import type { IngredientEcsBreakdown } from "src/api/client-api/products/useProductLivePreview";
import type { ProductLivePreview } from "src/api/client-api/products/useProductLivePreview";
import { useEnabledTransportStages } from "src/api/client-api/transport/useEnabledTransportStages";
import { useLanguage } from "src/context/LanguageProvider";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { ProductFormProductIngredient, ProductFormType } from "src/pages/Product/ProductForm/ProductForm.types";
import type { FieldError } from "../validation";
import { ECS_SCORE_ID } from "../ecobalyseMass";
import { useIngredientTypeDisplay } from "./useIngredientTypeDisplay";
import { IngredientOriginSelect } from "./IngredientOriginSelect";
import { EcosystemServicesDisclosure, formatImpactPts, ImpactRow, QuantityField } from "./IngredientCardV2Parts";
import { resolveIngredientOriginDefault, resolveIngredientOriginValue } from "./resolveIngredientOrigin";
import {
    ProductTransportInlineSelector,
    type TransportMaterializationPendingChange,
} from "../Steps/ProductTransportInlineSelector";
import { getProductIngredientTransportKey } from "../Steps/productIngredientTransportKey";

type IngredientCardV2Props = {
    product: ProductFormType;
    setProduct: Dispatch<SetStateAction<ProductFormType>>;
    disabled?: boolean;
    productIngredient: ProductFormProductIngredient;
    ingredientError?: FieldError;
    quantityError?: boolean;
    ecsBreakdown?: IngredientEcsBreakdown;
    ingredientLineScore?: string | null;
    transportLineScore: string | null;
    resolvedTransport?: ProductLivePreview["resolvedTransports"][number];
    scoreUnit: string;
    onEdit: () => void;
    onRemove: () => void;
    onUsedQuantityChange: (value: string) => void;
    onQuantityChange: (value: string) => void;
    onOriginChange: (origin: string | null) => void;
    onTransportMaterializationPendingChange?: TransportMaterializationPendingChange;
};

/** Product V2 Recipe card — Figma filled instance `13154:221702`. */
export const IngredientCardV2 = (props: IngredientCardV2Props) => {
    const {
        product,
        setProduct,
        disabled = false,
        productIngredient,
        ingredientError,
        quantityError = false,
        ecsBreakdown,
        ingredientLineScore = null,
        transportLineScore,
        resolvedTransport,
        scoreUnit,
        onEdit,
        onRemove,
        onUsedQuantityChange,
        onQuantityChange,
        onOriginChange,
        onTransportMaterializationPendingChange,
    } = props;
    const theme = useTheme();
    const { t } = useTranslation();
    const { currentLanguage } = useLanguage();
    const { lastSelectedVariation } = useEntityScores();
    const { isStageEnabled } = useEnabledTransportStages();
    const isIngredientTransportEnabled = isStageEnabled("ingredient_to_processing_site");

    const entityName =
        productIngredient.intermediateProduct?.title ??
        productIngredient.ingredient?.name ??
        productIngredient.ingredientReference?.name ??
        "-";
    const originDefault = resolveIngredientOriginDefault(productIngredient);
    const originValue = resolveIngredientOriginValue(productIngredient);
    const ingredientAnchor =
        productIngredient["@id"] != null && productIngredient.id != null ?
            { "@id": productIngredient["@id"], id: productIngredient.id }
        :   null;
    const draftIngredientKey = getProductIngredientTransportKey(productIngredient);
    const showEcsBreakdown = lastSelectedVariation.scoreId === ECS_SCORE_ID && ecsBreakdown !== undefined;
    const showNonEcsTotalImpact =
        lastSelectedVariation.scoreId !== ECS_SCORE_ID &&
        productIngredient.usedQuantity !== null &&
        productIngredient.usedQuantity !== 0;
    const showImpactFooter = showEcsBreakdown || showNonEcsTotalImpact || isIngredientTransportEnabled;
    const unavailableIngredientScoreTooltip = t(
        "product.ingredient_nonmonolithic_reference_without_score.title",
        "This ingredient has no reference value for the selected score.",
    );
    const green600 = theme.custom.green[600];
    const ingredientTypes = useIngredientTypeDisplay();
    const ingredientType =
        ingredientTypes[productIngredient.intermediateProduct != null ? "intermediate-product" : "raw-material"];

    return (
        <Box
            data-testid="product-v2-ingredient-card"
            sx={{
                backgroundColor: theme.custom.grey[200],
                borderRadius: theme.spacing(1.5),
                isolation: "isolate",
                overflow: "hidden",
                position: "relative",
            }}
        >
            <Stack
                sx={{
                    gap: theme.spacing(2),
                    padding: theme.spacing(2),
                    position: "relative",
                }}
            >
                <Stack direction="row" alignItems="flex-start" sx={{ gap: theme.spacing(2) }}>
                    <Stack flex={1} minWidth={0} sx={{ gap: theme.spacing(1) }}>
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ gap: theme.spacing(1), flexWrap: "wrap" }}
                        >
                            <Typography variant="body-semibold">
                                {t("product.v2.recipe.ingredient_label", "Ingredient*")}
                            </Typography>
                            <FoodsTags
                                label={ingredientType.singularLabel}
                                startIcon={ingredientType.icon}
                                variant="tags-white"
                            />
                        </Stack>
                        <Button
                            variant="select"
                            endIcon={<FoodsIcon icon="arrowDownShort" size={2} />}
                            onClick={onEdit}
                            sx={{
                                backgroundColor: theme.custom.grey[100],
                                borderColor: theme.custom.strokes.containerDivider.color,
                                borderRadius: theme.spacing(1),
                                color: theme.custom.grey[2800],
                                justifyContent: "space-between",
                                paddingInlineEnd: theme.spacing(1.5),
                                paddingInlineStart: theme.spacing(2.5),
                                width: "100%",
                            }}
                        >
                            <Typography variant="body-regular" noWrap>
                                {entityName}
                            </Typography>
                        </Button>
                        {ingredientError !== undefined && (
                            <Typography role="alert" variant="small-regular" sx={{ color: theme.palette.error.main }}>
                                {ingredientError.message ?? t(ingredientError.messageKey, "Invalid ingredient")}
                            </Typography>
                        )}
                    </Stack>
                    <FoodsDeleteButton
                        ariaLabel={t("product.recipe.remove_ingredient", "Remove ingredient")}
                        onClick={onRemove}
                    />
                </Stack>

                {isIngredientTransportEnabled && (
                    <IngredientOriginSelect
                        value={productIngredient.origin}
                        defaultOrigin={originDefault}
                        onChange={onOriginChange}
                        disabled={draftIngredientKey === null && ingredientAnchor === null}
                    />
                )}

                <Stack direction="row" sx={{ gap: theme.spacing(2) }}>
                    <QuantityField
                        label={t(
                            "product.v2.recipe.used_quantity_during_processing",
                            "Used quantity during processing",
                        )}
                        value={productIngredient.usedQuantity}
                        onChange={onUsedQuantityChange}
                    />
                    <QuantityField
                        label={t("product.v2.recipe.quantity_after_processing", "Quantity after processing*")}
                        value={productIngredient.quantity}
                        required
                        error={quantityError}
                        onChange={onQuantityChange}
                    />
                </Stack>

                <Box
                    aria-hidden
                    sx={{
                        borderBottomColor: theme.custom.strokes.containerDivider.color,
                        borderBottomStyle: theme.custom.strokes.containerDivider.style,
                        borderBottomWidth: theme.custom.strokes.containerDivider.width,
                        bottom: 0,
                        insetInline: 0,
                        pointerEvents: "none",
                        position: "absolute",
                    }}
                />
            </Stack>

            {showImpactFooter && (
                <Stack
                    sx={{
                        backgroundColor: theme.custom.grey[100],
                        gap: theme.spacing(1),
                        paddingY: theme.spacing(2),
                    }}
                >
                    {showEcsBreakdown && (
                        <Stack sx={{ gap: theme.spacing(0), paddingX: theme.spacing(2) }}>
                            <ImpactRow
                                label={t("product.v2.recipe.gross_impact", "Gross impact of the ingredient")}
                                value={formatImpactPts(ecsBreakdown.grossImpactPts, currentLanguage)}
                                unit={scoreUnit}
                            />
                            <EcosystemServicesDisclosure
                                breakdown={ecsBreakdown}
                                currentLanguage={currentLanguage}
                                green={green600}
                                scoreUnit={scoreUnit}
                            />
                            <ImpactRow
                                label={t("product.v2.recipe.total_impact", "Total impact of the ingredient")}
                                value={formatImpactPts(ecsBreakdown.totalImpactPts, currentLanguage)}
                                unit={scoreUnit}
                                labelVariant="semibold"
                                valueSize="small"
                            />
                        </Stack>
                    )}

                    {showNonEcsTotalImpact && (
                        <Stack sx={{ paddingX: theme.spacing(2) }}>
                            <ImpactRow
                                label={t("product.v2.recipe.total_impact", "Total impact of the ingredient")}
                                value={ingredientLineScore}
                                unit={scoreUnit}
                                labelVariant="semibold"
                                valueSize="small"
                                valueTooltip={
                                    ingredientLineScore === null ? unavailableIngredientScoreTooltip : undefined
                                }
                            />
                        </Stack>
                    )}

                    {isIngredientTransportEnabled && (
                        <>
                            <Box aria-hidden sx={{ height: theme.spacing(0), position: "relative", width: "100%" }}>
                                <Box
                                    sx={{
                                        borderBottomColor: theme.custom.strokes.containerDivider.color,
                                        borderBottomStyle: "dashed",
                                        borderBottomWidth: theme.custom.strokes.containerDivider.width,
                                        insetInline: 0,
                                        position: "absolute",
                                        top: 0,
                                    }}
                                />
                            </Box>

                            <Stack sx={{ gap: theme.spacing(0), paddingX: theme.spacing(2) }}>
                                <ProductTransportInlineSelector
                                    product={product}
                                    setProduct={setProduct}
                                    stage="ingredient_to_processing_site"
                                    anchor={{ type: "ingredient", value: ingredientAnchor }}
                                    draftAnchorKey={ingredientAnchor === null ? draftIngredientKey : null}
                                    resolvedTransport={resolvedTransport}
                                    disabled={disabled || draftIngredientKey === null}
                                    lineScore={transportLineScore}
                                    scoreUnit={scoreUnit}
                                    originCountryCode={originValue}
                                    alignScoreColumn
                                    scoreOnly
                                    onMaterializationPendingChange={onTransportMaterializationPendingChange}
                                />
                                <Typography variant="small-regular" sx={{ color: theme.custom.grey[2000] }}>
                                    {t(
                                        "product.v2.recipe.transport_not_in_ingredient_score",
                                        "Included in the Transport item — not in the ingredient score.",
                                    )}
                                </Typography>
                            </Stack>
                        </>
                    )}
                </Stack>
            )}

            <Box
                aria-hidden
                sx={{
                    borderColor: theme.custom.ingredientCard.stroke.color,
                    borderRadius: "inherit",
                    borderStyle: theme.custom.ingredientCard.stroke.style,
                    borderWidth: theme.custom.ingredientCard.stroke.width,
                    boxSizing: "border-box",
                    inset: 0,
                    pointerEvents: "none",
                    position: "absolute",
                }}
            />
        </Box>
    );
};
